import React, { useState, useEffect, useContext } from 'react';
import {
  FiMoreVertical, FiEye, FiLock, FiUnlock,
  FiUsers, FiAlertCircle, FiChevronLeft, FiChevronRight,
  FiTrash2, FiClock, FiCheckCircle, FiX, FiMapPin,
  FiPhone, FiMail, FiCalendar, FiFileText, FiBriefcase,
  FiExternalLink, FiShield,
} from 'react-icons/fi';
import { GiFarmTractor } from 'react-icons/gi';
import { FaWrench, FaDollarSign } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from "sweetalert2";
import AppContext from '../../../../../context/AppContext';

import '../../../../../assets/css/superadmin/usersmangment/rentalownermangment/rentalownerlist/RentalOwnerList.css';


// ─── Skeleton Loading Component ──────────────────────────────────────────────
const RentalOwnersListSkeleton = () => {
  return (
    <div className="rental-owners-list">
      <div className="rental-stats-grid">
        {[1, 2, 3, 4].map(i => (
          <div className="skeleton-stat-card" key={i}>
            <div className="skeleton skeleton-icon" />
            <div className="rental-stat-content">
              <div className="skeleton skeleton-value" />
              <div className="skeleton skeleton-label" />
            </div>
          </div>
        ))}
      </div>
      <div className="skeleton-tabs-wrapper">
        <div className="skeleton-tabs">
          <div className="skeleton skeleton-tab" />
          <div className="skeleton skeleton-tab" style={{ width: 160 }} />
          <div className="skeleton skeleton-tab" style={{ width: 180 }} />
        </div>
      </div>
      <div className="skeleton-table-wrapper">
        <div className="skeleton-table-header">
          {[140, 120, 80, 100, 100, 90, 50].map((w, i) => (
            <div className="skeleton skeleton-th" key={i} style={{ maxWidth: w }} />
          ))}
        </div>
        {[1, 2, 3, 4, 5, 6, 7].map(i => (
          <div className="skeleton-row" key={i}>
            <div className="skeleton skeleton-avatar" />
            <div className="skeleton-name-block">
              <div className="skeleton skeleton-name" />
              <div className="skeleton skeleton-id" />
            </div>
            <div className="skeleton skeleton-cell" />
            <div className="skeleton skeleton-cell" style={{ maxWidth: 60 }} />
            <div className="skeleton skeleton-cell" style={{ maxWidth: 80 }} />
            <div className="skeleton skeleton-badge" />
            <div className="skeleton skeleton-badge" />
            <div className="skeleton skeleton-action" />
          </div>
        ))}
      </div>
      <div className="skeleton-pagination">
        <div className="skeleton skeleton-pagination-info" />
        <div className="skeleton-pagination-btns">
          {[1, 2, 3, 4, 5].map(i => (
            <div className="skeleton skeleton-pagination-btn" key={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Helper ───────────────────────────────────────────────────────────────────
const getInitials = (name) =>
  (name || '')
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

// ─── PDF Viewer Modal ────────────────────────────────────────────────────────
const PdfViewerModal = ({ pdfUrl, onClose }) => {
  if (!pdfUrl) return null;

  return (
    <div className="pdf-viewer-overlay" onClick={onClose}>
      <div className="pdf-viewer-box" onClick={e => e.stopPropagation()}>
        <div className="pdf-viewer-header">
          <div>
            <h3 className="pdf-viewer-title">Verification Document</h3>
            <p className="pdf-viewer-subtitle">Submitted Document</p>
          </div>
          <div className="pdf-viewer-actions">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="pdf-open-link"
              title="Open in new tab"
            >
              <FiExternalLink size={15} /> Open
            </a>
            <button className="pdf-viewer-close" onClick={onClose}>
              <FiX size={17} />
            </button>
          </div>
        </div>
        <div className="pdf-viewer-frame">
          <iframe
            src={pdfUrl}
            title="Verification Document"
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        </div>
      </div>
    </div>
  );
};

// ─── KYC Detail Modal ────────────────────────────────────────────────────────
const KycDetailModal = ({ owner, onClose, onVerify, onReject, baseUrl, authToken, baseUrlForFile }) => {
  const [showPdf, setShowPdf] = useState(false);
  const navigate = useNavigate();
  // 👇 ab yeh asal DB state se start hote hain, sirf local toggle nahi
  const [cnicTicked, setCnicTicked] = useState(!!owner?.verifiedcnic);
  const [licenseTicked, setLicenseTicked] = useState(!!owner?.verifiedlisence);
  const [cnicLoading, setCnicLoading] = useState(false);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  if (!owner) return null;

  const pdfUrl = owner.document ? `${baseUrlForFile}/${owner.document}` : null;

  const infoFields = [
    { icon: <FiMail />, label: 'Email', value: owner.email },
    { icon: <FiPhone />, label: 'Phone', value: owner.phonenumber },
    { icon: <FiMapPin />, label: 'Address', value: owner.address },
    { icon: <FiBriefcase />, label: 'Owner ID', value: owner.id },
  ];

  // ── Verify one document (CNIC or License) ──
  const verifyDoc = (docType) => {
    const setLoading = docType === 'license' ? setLicenseLoading : setCnicLoading;
    const setTicked = docType === 'license' ? setLicenseTicked : setCnicTicked;
    setLoading(true);
    axios
      .post(`${baseUrl}/superadmin/kyc/verifyDocument`, {
        id: owner.id,
        actor_type: 'rental',
        doc_type: docType,
      }, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          setTicked(true);
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: res.data.message });
        }
      })
      .catch(() => {
        setLoading(false);
        Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong.' });
      });
  };

  // ── Reject one document (CNIC or License) with a reason, so the owner knows exactly what to fix ──
  const rejectDoc = (docType) => {
    const setLoading = docType === 'license' ? setLicenseLoading : setCnicLoading;
    const setTicked = docType === 'license' ? setLicenseTicked : setCnicTicked;

    Swal.fire({
      title: `Reject ${docType === 'license' ? 'License' : 'CNIC'}?`,
      input: 'text',
      inputLabel: 'Reason (shown to the rental owner)',
      inputPlaceholder: 'e.g. Image is blurry / details do not match',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      confirmButtonColor: '#dc3545',
    }).then((result) => {
      if (!result.isConfirmed) return;
      setLoading(true);
      axios
        .post(`${baseUrl}/superadmin/kyc/rejectDocument`, {
          id: owner.id,
          actor_type: 'rental',
          doc_type: docType,
          reason: result.value,
        }, { headers: { Authorization: `Bearer ${authToken}` } })
        .then((res) => {
          setLoading(false);
          if (res.data.success) {
            setTicked(false);
            Swal.fire({ icon: 'success', title: 'Rejected', text: `${docType === 'license' ? 'License' : 'CNIC'} rejected. The owner has been notified.` });
          } else {
            Swal.fire({ icon: 'error', title: 'Error', text: res.data.message });
          }
        })
        .catch(() => {
          setLoading(false);
          Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong.' });
        });
    });
  };

  const acceptRequest = () => {
    if (!cnicTicked || !licenseTicked) {
      Swal.fire({ icon: 'warning', title: 'Not ready', text: 'Both CNIC and License must be verified before approving this rental owner.' });
      return;
    }
    setVerifyLoading(true);
    const formData = new FormData();
    formData.append('id', owner.id);
    formData.append('verification_status', 'verified');
    axios
      .post(`${baseUrl}/superadmin/rentals/updateRentalVerificationStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({ icon: 'success', title: 'Success', text: 'Rental owner verified successfully!' });
          onVerify(owner.id);
          onClose();
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to verify rental owner.' });
        }
        setVerifyLoading(false);
      })
      .catch(() => {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to verify rental owner.' });
        setVerifyLoading(false);
      });
  };

  const rejectRequest = () => {
    setRejectLoading(true);
    const formData = new FormData();
    formData.append('id', owner.id);
    formData.append('verification_status', 'rejected');
    axios
      .post(`${baseUrl}/superadmin/rentals/updateRentalVerificationStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({ icon: 'success', title: 'Success', text: 'Rental owner rejected successfully!' });
          onReject(owner.id);
          onClose();
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to reject rental owner.' });
        }
        setRejectLoading(false);
      })
      .catch(() => {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to reject rental owner.' });
        setRejectLoading(false);
      });
  };

  return (
    <>
      <div className="kyc-modal-overlay" onClick={onClose}>
        <div className="kyc-modal-box" onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="kyc-modal-header">
            <div>
              <h2 className="kyc-modal-title">KYC Details</h2>
              <p className="kyc-modal-subtitle">Rental Owner Verification Documents</p>
            </div>
            <button className="kyc-modal-close" onClick={onClose}>
              <FiX size={18} />
            </button>
          </div>

          {/* Owner profile strip */}
          <div className="kyc-owner-profile">
            <div className="owner-avatar">
              {owner.image
                ? <img src={`${baseUrlForFile}/${owner.image}`} alt={owner.fullname} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : getInitials(owner.fullname)
              }
            </div>
            <div className="kyc-owner-meta">
              <h3>{owner.fullname}</h3>
              <p>{owner.email}</p>
              <span className={`status-badge ${owner.verification_status}`}>
                {cap(owner.verification_status)}
              </span>
            </div>
          </div>

          {/* Info grid */}
          <div className="kyc-info-grid">
            {infoFields.map((field, i) => (
              <div className="kyc-info-item" key={i}>
                <span className="kyc-info-icon">{field.icon}</span>
                <div>
                  <span className="kyc-info-label">{field.label}</span>
                  <span className="kyc-info-value">{field.value || '—'}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Document */}
          <div className="kyc-docs-section">
            <h4 className="kyc-docs-title">
              <FiFileText size={15} /> Submitted Document
            </h4>
            <p className="kyc-docs-hint">Click the document card to preview</p>

            <button
              className="kyc-doc-card clickable"
              onClick={() => pdfUrl && setShowPdf(true)}
              title="Click to view document"
            >
              <div className="kyc-doc-icon">
                <FiFileText size={22} />
              </div>
              <div className="kyc-doc-info">
                <span className="kyc-doc-name">Verification Document</span>
                <span className="kyc-doc-date">Uploaded by {owner.fullname}</span>
              </div>
              <div className="kyc-doc-right">
                <span className="kyc-doc-status submitted">Submitted</span>
                <span className="kyc-doc-view-hint">
                  <FiExternalLink size={13} /> View
                </span>
              </div>
            </button>

            {/* Verify CNIC & License buttons */}
            <div className="kyc-verify-doc-buttons">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <button
                  className={`kyc-doc-verify-btn ${cnicTicked ? 'ticked' : ''}`}
                  onClick={() => verifyDoc('cnic')}
                  disabled={cnicLoading || cnicTicked}
                >
                  <FiCheckCircle size={15} /> {cnicTicked ? 'CNIC Verified' : cnicLoading ? 'Verifying…' : 'Verify CNIC'}
                </button>
                {!cnicTicked && (
                  <button
                    className="kyc-doc-verify-btn"
                    style={{ fontSize: 11, color: '#dc3545', background: 'transparent', border: 'none', padding: 0 }}
                    onClick={() => rejectDoc('cnic')}
                    disabled={cnicLoading}
                  >
                    <FiX size={12} /> Reject CNIC
                  </button>
                )}
                {owner.cnic_rejection_reason && !cnicTicked && (
                  <span style={{ fontSize: 11, color: '#dc3545' }}>Last reason: {owner.cnic_rejection_reason}</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <button
                  className={`kyc-doc-verify-btn ${licenseTicked ? 'ticked' : ''}`}
                  onClick={() => verifyDoc('license')}
                  disabled={licenseLoading || licenseTicked}
                >
                  <FiCheckCircle size={15} /> {licenseTicked ? 'License Verified' : licenseLoading ? 'Verifying…' : 'Verify License'}
                </button>
                {!licenseTicked && (
                  <button
                    className="kyc-doc-verify-btn"
                    style={{ fontSize: 11, color: '#dc3545', background: 'transparent', border: 'none', padding: 0 }}
                    onClick={() => rejectDoc('license')}
                    disabled={licenseLoading}
                  >
                    <FiX size={12} /> Reject License
                  </button>
                )}
                {owner.license_rejection_reason && !licenseTicked && (
                  <span style={{ fontSize: 11, color: '#dc3545' }}>Last reason: {owner.license_rejection_reason}</span>
                )}
              </div>
            </div>

            {/* Notice */}
            <div className="kyc-equipment-notice">
              <FiShield size={14} />
              <span>Both CNIC and license must be verified before approving the rental owner.</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="kyc-modal-actions">
            <button
              className="kyc-btn-approve"
              onClick={acceptRequest}
              disabled={verifyLoading || rejectLoading || !cnicTicked || !licenseTicked}
              title={(!cnicTicked || !licenseTicked) ? 'Verify both CNIC and License first' : ''}
            >
              {verifyLoading
                ? <><span className="btn-spinner" /> Verifying…</>
                : <><FiCheckCircle size={15} /> Verify Seller</>
              }
            </button>

            <button
              className="kyc-btn-reject"
              onClick={rejectRequest}
              disabled={verifyLoading || rejectLoading}
            >
              {rejectLoading
                ? <><span className="btn-spinner btn-spinner-red" /> Rejecting…</>
                : <><FiX size={15} /> Reject Verification</>
              }
            </button>

            <button className="kyc-btn-cancel" onClick={onClose}>
              Close
            </button>
          </div>

        </div>
      </div>

      {/* PDF Viewer */}
      {showPdf && (
        <PdfViewerModal
          pdfUrl={pdfUrl}
          onClose={() => setShowPdf(false)}
        />
      )}
    </>
  );
};

// ─── Main List Component ──────────────────────────────────────────────────────
const RentalOwnersList = ({ onViewDetails, onBlock, onViewEquipments }) => {
  const { baseUrl, authToken, baseUrlForFile } = useContext(AppContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [kycModalOwner, setKycModalOwner] = useState(null);
  const itemsPerPage = 10;

  // ── Fetch all rental owners from API ──


  // ── Fetch all rental owners from API ──
  const viewAllRentals = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/superadmin/rentals/viewAllRentals`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          // 👇 businessname backend se pehle hi eager-loaded aa raha hai
          // (Srrentaladmin::with('srbusinessdetails')) — is liye companyName
          // yahan turant, synchronously set karna hai. Pehle yeh sirf us
          // alag/slow "viewOwnerEquipments" call ke resolve hone ka intezar
          // karta tha, jo ek race condition thi — company name pehle 1-2
          // second ke liye flash hota, phir agar koi doosra refetch beech
          // me aa jata to wapas "—" ho jata.
          const rentals = response.data.rentals.map((owner) => ({
            ...owner,
            companyName: owner.srbusinessdetails?.businessname || '—',
          }));
          setOwners(rentals);
          setLoading(false);

          // ✅ Equipment count abhi bhi alag se fetch karna hai (yeh eager-loaded nahi)
          rentals.forEach((owner) => {
            const formData = new FormData();
            formData.append('owner_id', owner.id);

            axios.post(`${baseUrl}/superadmin/rentals/viewOwnerEquipments`, formData, {
              headers: { Authorization: `Bearer ${authToken}` },
            }).then((res) => {
              if (res.data.success) {
                const equipments = res.data.equipments || [];
                setOwners((prev) =>
                  prev.map((o) =>
                    o.id === owner.id
                      ? {
                        ...o,
                        equipmentCount: equipments.length,
                        // companyName ko yahan chhedte nahi — already sahi set ho chuka hai
                        companyName: o.companyName === '—'
                          ? (equipments[0]?.shopname || '—')
                          : o.companyName,
                      }
                      : o
                  )
                );
              }
            }).catch(() => { });
          });
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load rental owners.' });
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load rental owners.' });
        setLoading(false);
      });
  };

  // const viewAllRentals = () => {
  //   setLoading(true);
  //   axios
  //     .get(`${baseUrl}/superadmin/rentals/viewAllRentals`, {
  //       headers: { Authorization: `Bearer ${authToken}` },
  //     })
  //     .then((response) => {
  //       if (response.data.success === true) {
  //         setOwners(response.data.rentals);
  //       } else {
  //         Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load rental owners.' });
  //       }
  //       setLoading(false);
  //     })
  //     .catch((error) => {
  //       console.log(error);
  //       Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load rental owners.' });
  //       setLoading(false);
  //     });
  // };

  // ── Toggle account status ──
  const handleToggleStatus = (ownerId) => {
    const formData = new FormData();
    formData.append('id', ownerId);
    axios
      .post(`${baseUrl}/superadmin/rentals/rentalStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          Swal.fire({ icon: 'success', title: 'Success', text: 'Rental owner status updated successfully!' });
          viewAllRentals();
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update rental owner status.' });
      });
  };

  useEffect(() => {
    viewAllRentals();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  if (loading) return <RentalOwnersListSkeleton />;

  // ── After verify/reject — reload fresh from API ──
  const handleVerifyOwner = () => {
    viewAllRentals();
  };

  const handleRejectOwner = () => {
    viewAllRentals();
  };

  // ── Tab pools ──
  const kycOwners = owners.filter(o => ['pending', 'rejected'].includes(o.verification_status?.toLowerCase()));
  const verifiedOwners = owners.filter(o => o.verification_status === 'verified');

  const pool =
    activeTab === 'all' ? owners :
      activeTab === 'verified' ? verifiedOwners :
        activeTab === 'kyc' ? kycOwners : owners;

  // ── Stats ──
  const totalOwners = owners.length;
  const pendingCount = kycOwners.length;
  const activeCount = owners.filter(o => o.account_status === 'active').length;
  const totalRevenue = owners.reduce((s, o) => s + (o.totalRevenue || 0), 0);

  // ── Pagination ──
  const totalPages = Math.ceil(pool.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOwners = pool.slice(startIndex, startIndex + itemsPerPage);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, currentPage]);
    if (currentPage > 1) pages.add(currentPage - 1);
    if (currentPage < totalPages) pages.add(currentPage + 1);
    return [...pages].sort((a, b) => a - b);
  };

  const pageNumbers = getPageNumbers();

  // 👇 "rejected" bhi include kiya — warna reject hone ke baad, chahe
  // seller ne document dobara submit kar diya ho, admin ko "View KYC
  // Details" ka option hi nahi milta tha dobara review karne ke liye.
  const shouldShowKycAction = (owner) =>
    ['pending', 'rejected'].includes(owner.verification_status?.toLowerCase());

  const showEquipmentForOwner = (owner) =>
    owner.verification_status?.toLowerCase() === 'verified';

  return (
    <div className="rental-owners-list">

      {/* ════════════ 0. PAGE HEADING ════════════ */}
      <div className="rental-page-header">
        <h2 className="rental-page-title">Rental Shop Management</h2>
        <p className="rental-page-subtitle">
          Manage rental shop owners, their equipment listings, and verification requests.
        </p>
      </div>

      {/* ════════════ 1. STAT CARDS ════════════ */}
      <div className="rental-stats-grid">

        <div className="rental-stat-card">
          <div className="stat-icon-wrap"><FiUsers size={28} /></div>
          <div className="rental-stat-content">
            <h3 className="stat-value">{totalOwners}</h3>
            <p className="stat-title">Total Owners</p>
          </div>
        </div>

        <div className="rental-stat-card">
          <div className="stat-icon-wrap"><FiAlertCircle size={28} /></div>
          <div className="rental-stat-content">
            <h3 className="stat-value">{pendingCount}</h3>
            <p className="stat-title">Pending Requests</p>
          </div>
        </div>

        <div className="rental-stat-card">
          <div className="stat-icon-wrap"><FaDollarSign size={28} /></div>
          <div className="rental-stat-content">
            <h3 className="stat-value">Rs {totalRevenue.toLocaleString()}</h3>
            <p className="stat-title">Total Earnings</p>
          </div>
        </div>

        <div className="rental-stat-card">
          <div className="stat-icon-wrap"><FaWrench size={28} /></div>
          <div className="rental-stat-content">
            <h3 className="stat-value">{activeCount}</h3>
            <p className="stat-title">Active Owners</p>
          </div>
        </div>

      </div>

      {/* ════════════ 2. TABS ════════════ */}
      <div className="owners-tabs-wrapper">
        <div className="owners-tabs">

          <button
            className={`owners-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            <GiFarmTractor size={15} />
            All Sellers
            <span className="tab-badge">{owners.length}</span>
          </button>

          <button
            className={`owners-tab ${activeTab === 'verified' ? 'active' : ''}`}
            onClick={() => handleTabChange('verified')}
          >
            <FiShield size={15} />
            Verified Sellers
            {verifiedOwners.length > 0 && (
              <span className="tab-badge verified-tab-badge">{verifiedOwners.length}</span>
            )}
          </button>

          <button
            className={`owners-tab ${activeTab === 'kyc' ? 'active' : ''}`}
            onClick={() => handleTabChange('kyc')}
          >
            <FiClock size={15} />
            KYC Pending Requests
            {kycOwners.length > 0 && (
              <span className="tab-badge kyc-badge">{kycOwners.length}</span>
            )}
          </button>

        </div>
      </div>

      {/* ════════════ 3. TABLE ════════════ */}
      <div className="owners-table-wrapper">
        <table className="owners-table">
          <thead>
            <tr>
              <th>Seller Name</th>
              <th>Company</th>
              <th>Equipment</th>
              {activeTab !== 'kyc' && <th>Avg Rating</th>}
              {activeTab !== 'kyc' && <th>Total Revenue</th>}
              <th>Verification Status</th>
              <th>Account Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOwners.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="no-results">
                    <FiUsers size={44} />
                    <p>
                      {activeTab === 'kyc' ? 'No KYC pending requests' :
                        activeTab === 'verified' ? 'No verified sellers yet' :
                          'No sellers found'}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedOwners.map(owner => (
                <tr key={owner.id} className={owner.account_status === 'inactive' ? 'blocked-row' : ''}>

                  {/* Seller Name */}
                  <td className="owner-cell">
                    <div className="owner-info">
                      <div className="owner-avatar">
                        {owner.image
                          ? <img src={`${baseUrlForFile}/${owner.image}`} alt={owner.fullname} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                          : getInitials(owner.fullname)
                        }
                      </div>
                      <div className="owner-details">
                        <span className="owner-name">{owner.fullname}</span>
                        <span className="owner-id">ID: {owner.id}</span>
                      </div>
                    </div>
                  </td>

                  {/* Company */}

                  <td>
                    <div className="company-info">
                      <span className="company-name">
                        {owner.companyName || owner.srbusinessdetails?.businessname || '—'}
                      </span>
                    </div>
                  </td>
                  {/* Equipment */}
                  <td>
                    {showEquipmentForOwner(owner)
                      ? <span className="equipment-count">{owner.totalEquipment || 0} Units</span>
                      : <span className="equipment-na">—</span>
                    }
                  </td>

                  {/* Avg Rating */}
                  {activeTab !== 'kyc' && (
                    <td>
                      {owner.averageRating > 0
                        ? <span className="rating-value">⭐ {owner.averageRating}</span>
                        : <span className="equipment-na">—</span>
                      }
                    </td>
                  )}

                  {/* Total Revenue */}
                  {activeTab !== 'kyc' && (
                    <td className="revenue-cell">Rs {(owner.totalRevenue || 0).toLocaleString()}</td>
                  )}

                  {/* Verification Status */}
                  <td>
                    <span className={`status-badge ${owner.verification_status}`}>
                      {cap(owner.verification_status)}
                    </span>
                  </td>

                  {/* Account Status */}
                  <td>
                    <span className={`account-badge ${owner.account_status === 'inactive' ? 'blocked-acc' : 'active-acc'}`}>
                      {owner.account_status === 'inactive' ? 'Blocked' : 'Active'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td>
                    <div className="actions-menu-container">
                      <button
                        className="actions-menu-btn"
                        onClick={e => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === owner.id ? null : owner.id);
                        }}
                      >
                        <FiMoreVertical size={18} />
                      </button>

                      {openMenuId === owner.id && (
                        <div className="actions-dropdown">

                          {shouldShowKycAction(owner) ? (
                            <button
                              className="action-item"
                              onClick={() => { setKycModalOwner(owner); setOpenMenuId(null); }}
                            >
                              <FiEye size={14} /> View KYC Details
                            </button>
                          ) : (
                            <button
                              className="action-item"
                              onClick={() => {
                                navigate('/superadmin/users/rental-owners/equipments', { state: { owner, ownerId: owner.id } });
                                setOpenMenuId(null);
                              }}
                            >
                              <GiFarmTractor size={14} /> View Equipments
                            </button>
                          )}

                          <button
                            className="action-item"
                            onClick={() => { handleToggleStatus(owner.id); setOpenMenuId(null); }}
                          >
                            {owner.account_status === 'inactive'
                              ? <><FiUnlock size={14} /> Unblock</>
                              : <><FiLock size={14} /> Block</>
                            }
                          </button>

                          <button
                            className="action-item danger"
                            onClick={() => setOpenMenuId(null)}
                          >
                            <FiTrash2 size={14} /> Delete
                          </button>

                        </div>
                      )}
                    </div>
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ════════════ 4. PAGINATION ════════════ */}
      {pool.length > 0 && (
        <div className="pagination-footer">
          <span className="pagination-info">
            Showing {Math.min(startIndex + 1, pool.length)}–
            {Math.min(startIndex + itemsPerPage, pool.length)} of {pool.length} results
          </span>

          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <FiChevronLeft size={17} />
            </button>

            {pageNumbers.map((num, idx) => (
              <React.Fragment key={num}>
                {idx > 0 && pageNumbers[idx - 1] !== num - 1 && (
                  <span className="pagination-dots">…</span>
                )}
                <button
                  className={`pagination-number ${currentPage === num ? 'active' : ''}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              </React.Fragment>
            ))}

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              <FiChevronRight size={17} />
            </button>
          </div>
        </div>
      )}

      {/* ════════════ 5. KYC MODAL ════════════ */}
      {kycModalOwner && (
        <KycDetailModal
          owner={kycModalOwner}
          onClose={() => setKycModalOwner(null)}
          onVerify={handleVerifyOwner}
          onReject={handleRejectOwner}
          baseUrl={baseUrl}
          authToken={authToken}
          baseUrlForFile={baseUrlForFile}
        />
      )}

    </div>
  );
};

export default RentalOwnersList;